import { IFrameEventType, isOrbyEventTypeKey, OrbyLinkEventType, OAuthPermissionLevel, OrbyLinkOperation, } from "../../utils/types.js";
import { addPopup, removePopup, backdropId } from "../../utils/popup.js";
import { extractAccountCluster } from "@orb-labs/orby-core";
import { generateUUID, isValidUUID } from "../../utils/crypto.js";
let currentOptions;
let orbyOrigin;
async function handleOrbyEvent(event) {
    // Handle events from oauth-backend iframe
    // The oauth-backend only sends: loaded, integrationConnectionError, integrationOAuthStarted,
    // front-oauth-data, brokerageAccountAccessToken, done, and close
    switch (event.data.type) {
        case IFrameEventType.OpenOAuthPopup: {
            const payload = event.data.payload;
            const { url } = payload;
            // Open popup
            const popup = window.open(url, "oauth", "width=600,height=700");
            if (!popup) {
                console.error("Popup blocked");
            }
            break;
        }
        case IFrameEventType.BrokerageAccountAccessToken: {
            const payload = event.data.payload;
            currentOptions?.onEvent?.({
                type: OrbyLinkEventType.IntegrationConnected,
                payload,
            });
            const integrationConnectedEvent = {
                accountCluster: extractAccountCluster(payload.accountCluster),
                tokens: payload.tokens,
            };
            currentOptions?.onIntegrationConnected?.(integrationConnectedEvent);
            break;
        }
        case IFrameEventType.Done: {
            const payload = event.data?.payload;
            currentOptions?.onExit?.(payload?.errorMessage, payload);
            removePopup();
            break;
        }
        case IFrameEventType.Close: {
            const payload = event.data?.payload;
            currentOptions?.onEvent?.({
                type: OrbyLinkEventType.Close,
                payload: event.data.payload,
            });
            currentOptions?.onExit?.(payload?.errorMessage, payload);
            removePopup();
            break;
        }
        case IFrameEventType.Loaded: {
            // Iframe has loaded - notify the application
            currentOptions?.onEvent?.({ type: OrbyLinkEventType.PageLoaded });
            break;
        }
        case IFrameEventType.IntegrationConnectionError: {
            const payload = event.data.payload;
            currentOptions?.onEvent?.({
                type: OrbyLinkEventType.IntegrationConnectionError,
                payload: {
                    errorMessage: payload?.errorMessage || "Unknown error occurred",
                },
            });
            currentOptions?.onExit?.(payload?.errorMessage || "Connection error");
            break;
        }
        case IFrameEventType.IntegrationOAuthStarted: {
            currentOptions?.onEvent?.({
                type: OrbyLinkEventType.IntegrationOAuthStarted,
            });
            break;
        }
        default: {
            if (isOrbyEventTypeKey(event.data.type)) {
                currentOptions?.onEvent?.({
                    type: event.data.type,
                    payload: event.data.payload,
                });
            }
            break;
        }
    }
}
async function eventsListener(event) {
    // Verify origin matches our backend
    if (currentOptions?.baseUrl) {
        try {
            const backendOrigin = new URL(currentOptions.baseUrl).origin;
            if (event.origin !== backendOrigin && event.origin !== orbyOrigin) {
                console.warn("Received message from untrusted origin:", event.origin);
                return;
            }
        }
        catch (e) {
            console.warn("Invalid baseUrl in options:", e);
        }
    }
    await handleOrbyEvent(event);
}
export const createOrbyLinkWeb = (options) => {
    if (!options.baseUrl) {
        throw new Error("baseUrl is required in OrbyOptions");
    }
    const closeOrbyLink = () => {
        // Remove backdrop click listener before removing popup
        const backdrop = document.getElementById(backdropId);
        if (backdrop) {
            backdrop.removeEventListener("click", closeOrbyLink);
        }
        removePopup();
        window.removeEventListener("message", eventsListener);
        options.onExit?.();
    };
    const linkAccount = (accountClusterId, sessionId, customIframeId) => {
        openOrbyLink(OAuthPermissionLevel.READ, OrbyLinkOperation.LINK_ACCOUNT, accountClusterId, sessionId, customIframeId);
    };
    const deposit = (paymentInfo, accountClusterId, sessionId, customIframeId) => {
        openOrbyLink(OAuthPermissionLevel.WRITE, OrbyLinkOperation.DEPOSIT, accountClusterId, sessionId, customIframeId, undefined, paymentInfo);
    };
    const payWithCrypto = (paymentInfo, accountClusterId, sessionId, customIframeId) => {
        openOrbyLink(OAuthPermissionLevel.WRITE, OrbyLinkOperation.PAY_WITH_CRYPTO, accountClusterId, sessionId, customIframeId, undefined, paymentInfo);
    };
    const openOrbyLink = (permissions, operation, accountClusterId, sessionId, customIframeId, platformId, paymentInfo) => {
        // Generate sessionId if not provided
        let finalSessionId = sessionId;
        if (!finalSessionId) {
            finalSessionId = generateUUID();
        }
        else if (!isValidUUID(finalSessionId)) {
            options?.onExit?.("Invalid sessionId format! Must be a valid UUID.");
            return;
        }
        currentOptions = options;
        // Build link URL pointing to oauth-backend
        const baseUrl = options.baseUrl.replace(/\/$/, ""); // Remove trailing slash
        const parentOrigin = window.location.origin;
        // Build query parameters, only including those that are defined
        const params = new URLSearchParams();
        params.set("sessionId", finalSessionId);
        params.set("parentOrigin", parentOrigin);
        params.set("operation", operation);
        if (accountClusterId) {
            params.set("accountClusterId", accountClusterId);
        }
        if (paymentInfo) {
            params.set("paymentTokenInfo", JSON.stringify(paymentInfo));
        }
        if (permissions) {
            params.set("permissions", permissions);
        }
        if (platformId) {
            params.set("platformId", platformId);
        }
        const linkUrl = `${baseUrl}/link?${params.toString()}`;
        // Add language if specified
        const finalLinkUrl = addLanguage(linkUrl, currentOptions?.language);
        orbyOrigin = new URL(baseUrl).origin;
        window.removeEventListener("message", eventsListener);
        if (customIframeId) {
            const iframe = document.getElementById(customIframeId);
            if (iframe) {
                iframe.allow = "clipboard-read *; clipboard-write *";
                iframe.src = finalLinkUrl;
            }
            else {
                console.warn(`OrbyKit: No iframe found with id ${customIframeId}`);
            }
        }
        else {
            addPopup(finalLinkUrl);
            // Add click handler to backdrop to close when clicking outside iframe
            const backdrop = document.getElementById(backdropId);
            if (backdrop) {
                backdrop.addEventListener("click", closeOrbyLink);
            }
        }
        window.addEventListener("message", eventsListener);
    };
    return { openOrbyLink, closeOrbyLink, linkAccount, deposit, payWithCrypto };
};
function addLanguage(linkUrl, language) {
    if (language === "system") {
        language =
            typeof navigator !== "undefined" && navigator.language ? encodeURIComponent(navigator.language) : undefined;
    }
    return `${linkUrl}${linkUrl.includes("?") ? "&" : "?"}lng=${language || "en"}`;
}
